import {ContentEditableEditorPlugin, spliceTextNode} from '@frontapp/editor-html';

import {findAncestor, isElement} from '../../../../../../libs/shared/helpers/src/browser/domHelpers';
import {
  isElectron,
  isElectronVersionGTE,
} from '../../../../../../libs/shared/helpers/src/environment/platformHelpers';
import {HandlerOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {
  VariableInterpolator,
  variableRegexp,
} from '../../../../../services/variableInterpolation/variableInterpolator';
import {surveyTemplateVariable} from '../../../../inboxes/composer/signatures/constants';

export const answerVariablePluginDataAttribute = 'data-front-decoration-variable';

export type RawVariable = string;

export function createAnswerVariablePlugin(
  getInterpolator: () => VariableInterpolator,
  replacePilledVariablesInMessageTemplates: boolean,
  onRawVariables?: HandlerOf<readonly RawVariable[]>,
  onNewRawVariable?: HandlerOf<RawVariable>,
): ContentEditableEditorPlugin {
  return {
    processText: (element, textNodeAndCursorPositions) => {
      const document = element.ownerDocument;
      if (!document) {
        return;
      }

      const interpolator = getInterpolator();

      Array.from(textNodeAndCursorPositions).forEach(({node}) => {
        processVariablePlaceholders(
          node,
          document,
          interpolator,
          onNewRawVariable,
          replacePilledVariablesInMessageTemplates,
        );
      });
    },
    processTree: (element) => {
      const interpolator = getInterpolator();
      processAnchors(element, interpolator);
      onRawVariables?.(findRawVariables(getVariables(element)));
    },
  };
}

function getVariables(container: Element) {
  return Array.from(container.querySelectorAll(`[${answerVariablePluginDataAttribute}]`));
}

function findRawVariables(elements: Element[]) {
  return (
    elements
      // Use `.textContent` because text may be nested in anchors.
      .map((node) => node.textContent || '')
      .filter(shouldAlertRawVariable)
      .filter((text) => VariableInterpolator.hasRawVariables(text))
  );
}

export function maybeSelectPillText(target: EventTarget, container: HTMLElement) {
  if (!isElement(target)) {
    return;
  }

  const document = target.ownerDocument;
  const window = document && document.defaultView;

  if (!document || !window) {
    return;
  }

  const pill = findAncestor(target, container, isPillElement);

  if (!pill) {
    return;
  }

  const selection = window.getSelection();

  if (!selection) {
    return;
  }

  const range = document.createRange();
  range.selectNode(pill);
  selection.removeAllRanges();
  selection.addRange(range);
}

export function updateAnswerVariables(
  container: HTMLElement,
  interpolator: VariableInterpolator,
): readonly RawVariable[] {
  const elements = getVariables(container);
  elements.forEach((node) => {
    const variableName = node.getAttribute(answerVariablePluginDataAttribute);

    if (!variableName) {
      return;
    }

    const variablePlaceholder = `{{${variableName}}}`;
    const interpolatedValue = interpolator.interpolateText(variablePlaceholder);

    /* eslint no-param-reassign: 0 */
    node.textContent = interpolatedValue || variablePlaceholder;
  });
  return findRawVariables(elements);
}

function processVariablePlaceholders(
  textNode: Text,
  document: Document,
  interpolator: VariableInterpolator,
  onNewRawVariable?: HandlerOf<RawVariable>,
  replacePilledVariablesInMessageTemplates?: boolean,
) {
  let match;
  // eslint-disable-next-line
  while ((match = variableRegexp.exec(textNode.data))) {
    // Always search from the beginning of the textNode data as we're splicing it each time we find a match.
    variableRegexp.lastIndex = 0;

    const before = match[0];
    const variableName = match[1];
    const interpolatedValue = interpolator.interpolateText(before);
    const after = interpolatedValue || before;

    // Already decorated, just replace the variable if necessary.
    if (textNode.parentElement && isPillElement(textNode.parentElement)) {
      if (replacePilledVariablesInMessageTemplates && before !== after) {
        textNode.data = after;
      }
      break;
    }

    const node = document.createElement('span');
    node.setAttribute(answerVariablePluginDataAttribute, variableName);
    node.setAttribute('contenteditable', 'false');
    node.textContent = after;

    // Wrapping within span in electron to prevent an old chromium bug concerning trailing non editable content within editable content.
    // In electron 5, this makes the cursor jump to the end of the line (PB-15577).
    // TODO: remove this hack when upgrading to electron 7.
    if (isElectron() && !isElectronVersionGTE('7.0.0')) {
      const wrappingNode = document.createElement('span');
      wrappingNode.appendChild(node);
      spliceTextNode(textNode, match.index, before.length, wrappingNode);
    } else {
      spliceTextNode(textNode, match.index, before.length, node);
    }

    if (onNewRawVariable && before === after && shouldAlertRawVariable(before)) {
      onNewRawVariable(before);
    }
  }
}

function shouldAlertRawVariable(text: string) {
  return text !== surveyTemplateVariable;
}

function isPillElement(element: Element): element is HTMLSpanElement {
  return element.hasAttribute(answerVariablePluginDataAttribute);
}

function processAnchors(element: Element, interpolator: VariableInterpolator) {
  element.querySelectorAll('a').forEach((anchor) => {
    // Use the attribute instead of the href property as the latter gets normalized by the browser.
    const currentHref = anchor.getAttribute('href');

    if (!currentHref) {
      return;
    }

    const updatedHref = interpolator.interpolateText(currentHref);

    if (updatedHref === currentHref) {
      return;
    }

    anchor.setAttribute('href', updatedHref);
  });
}
